import { Link } from 'react-router-dom';
import type { CityCount, EventSummary } from '@mmef/shared/api';
import { describePrice } from '@mmef/shared/api';
import { useGetOverviewQuery } from './eventsApi';
import { originFor } from './useOverview';
import {
  calendarDay,
  HOME_HEADING,
  homeSummary,
  otherCitiesLabel,
  upcomingTags,
} from './homeMetadata';

/*
 * The homepage before anyone searches, in the pieces the events page arranges around its search
 * bar and map.
 *
 * <p><b>This has a twin.</b> HomePageHtml.java renders the same markup on the server, so the page
 * arrives with its content in it. Class names, wording and structure match on purpose — when these
 * replace the server's copy, nothing should visibly move. Change one, change the other.
 */

export function OverviewHeading() {
  const { data } = useGetOverviewQuery();
  return (
    <div className="page-head">
      <h1 className="page-head__title">{HOME_HEADING}</h1>
      {data && <p className="page-head__summary">{homeSummary(data)}</p>}
    </div>
  );
}

export function CityList({ onPick }: { onPick: (city: CityCount) => void }) {
  const { data } = useGetOverviewQuery();
  if (!data) return null;

  return (
    <div className="home__cities">
      <h2 className="home__label">Browse by city</h2>
      <ul className="city-list">
        {data.cities.map((city) => (
          <li key={city.name}>
            <CityRow name={city.name} count={city.count} onPick={originFor(city) ? () => onPick(city) : undefined} />
          </li>
        ))}
        {data.otherCities.length > 0 && (
          <li>
            <CityRow name={otherCitiesLabel(data.otherCities)} count={data.otherCities.length} other />
          </li>
        )}
      </ul>
    </div>
  );
}

function CityRow({
  name,
  count,
  other = false,
  onPick,
}: {
  name: string;
  count: number;
  other?: boolean;
  onPick?: () => void;
}) {
  const className = `city-list__row${other ? ' city-list__row--other' : ''}`;
  const content = (
    <>
      <span className="city-list__name">{name}</span>
      <span className="city-list__count">{count}</span>
    </>
  );
  // A button only when there is somewhere to go. The server renders every row as plain text; the
  // two look identical, so the swap is invisible.
  return onPick ? (
    <button
      type="button"
      className={`${className} city-list__row--button`}
      onClick={onPick}
      aria-label={`${name}, ${count} event${count === 1 ? '' : 's'}`}
    >
      {content}
    </button>
  ) : (
    <span className={className}>{content}</span>
  );
}

export function UpcomingEvents({ onSeeAll }: { onSeeAll: () => void }) {
  const { data } = useGetOverviewQuery();
  if (!data || data.upcoming.length === 0) return null;

  return (
    <section className="upcoming" aria-labelledby="upcoming-title">
      <div className="upcoming__header">
        <h2 className="upcoming__title-heading" id="upcoming-title">
          Coming up next
        </h2>
        <button type="button" className="button button--link" onClick={onSeeAll}>
          See all {data.totalEvents} events
        </button>
      </div>
      <ol className="upcoming__list">
        {data.upcoming.map((event) => (
          <UpcomingRow key={event.id} event={event} />
        ))}
      </ol>
    </section>
  );
}

function UpcomingRow({ event }: { event: EventSummary }) {
  const { month, day } = calendarDay(event.startTime, event.timezone);
  const meta = [event.city, event.venueName, event.organiser]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' · ');
  const tags = upcomingTags(event.keywords);
  const price = describePrice(event.price);

  return (
    <li className="upcoming__item">
      <time className="upcoming__date" dateTime={event.startTime}>
        <span className="upcoming__month">{month}</span>
        <span className="upcoming__day">{day}</span>
      </time>
      <div className="upcoming__body">
        <Link className="upcoming__name" to={`/events/${event.id}`}>
          {event.title}
        </Link>
        {meta && <p className="upcoming__meta">{meta}</p>}
        {tags.length > 0 && (
          <ul className="upcoming__tags">
            {tags.map((tag) => (
              <li className="upcoming__tag" key={tag}>
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
      {price && <span className="upcoming__price">{price}</span>}
    </li>
  );
}
